import { zonedDate } from '../../utils/date-time';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import AvatarUser from '../../components/AvatarUser';
import { useParamsContext } from '../../contexts/ParamsContext';
import { useRecorrencias } from './Context';
import { Recorrencia } from './types';
import {
    DIAS_ENCERRANDO,
    describeRegra,
    formatProximo,
    getEncerramentoLabel,
    getServiceNames,
    getSituacao,
    normalizeSearch,
} from './helpers';
import './Recorrencias.css';
import { ChevronRight, CircleCheck, RefreshCw, Search, TriangleAlert } from 'lucide-react';

type Aba = 'ativas' | 'encerrando' | 'encerradas';

const PAGE_SIZE = 30;

const abas: { key: Aba; label: string }[] = [
    { key: 'ativas', label: 'Ativas' },
    { key: 'encerrando', label: 'A encerrar' },
    { key: 'encerradas', label: 'Encerradas' },
];

const pertenceAba = (recorrencia: Recorrencia, aba: Aba) => {
    const situacao = getSituacao(recorrencia);

    if (aba === 'ativas') return situacao !== 'encerrada';
    return situacao === (aba === 'encerrando' ? 'encerrando' : 'encerrada');
};

const proximoValue = (recorrencia: Recorrencia) => {
    const proximo = recorrencia.resumo?.proximoStartAt;
    return proximo ? moment(proximo).valueOf() : Infinity;
};

const sorters: Record<Aba, (a: Recorrencia, b: Recorrencia) => number> = {
    ativas: (a, b) => proximoValue(a) - proximoValue(b),
    encerrando: (a, b) => moment(a.endAt).valueOf() - moment(b.endAt).valueOf(),
    encerradas: (a, b) => moment(b.endAt).valueOf() - moment(a.endAt).valueOf(),
};

const emptyMessages: Record<Aba, string> = {
    ativas: 'Nenhuma recorrência ativa no momento.',
    encerrando: `Nenhuma recorrência encerra nos próximos ${DIAS_ENCERRANDO} dias.`,
    encerradas: 'Nenhuma recorrência encerrada.',
};

const RecorrenciaRow = ({ recorrencia }: { recorrencia: Recorrencia }) => {
    const [servico, ...extras] = getServiceNames(recorrencia);
    const situacao = getSituacao(recorrencia);
    const clientName = recorrencia.clientName || 'Cliente removido';
    const { total = 0, concluidos = 0, proximoStartAt = null } = recorrencia.resumo || {};

    return (
        <li>
            <Link to={`/recorrencias/${recorrencia._id}`} className="rec-row">
                <span className="rec-row-avatar">
                    <AvatarUser name={clientName} photoURL={recorrencia.client?.photoURL} size="h-10 w-10 !text-sm" />
                </span>

                <span className="rec-cell rec-cell-client">
                    <span className="rec-title">{clientName}</span>
                    <span className="rec-sub">
                        {servico || 'Serviço não informado'}
                        {extras.length > 0 && ` +${extras.length}`}
                    </span>
                </span>

                <span className="rec-cell rec-cell-rule">
                    <span className="rec-text">{describeRegra(recorrencia)}</span>
                    <span className="rec-sub">até {zonedDate(recorrencia.endAt, recorrencia.timezone).format('DD/MM/YYYY')}</span>
                    {(situacao !== 'ativa' || !!recorrencia.assinaturaId) && (
                        <span className="sf-tags">
                            {situacao !== 'ativa' && (
                                <span className="sf-tag" data-tone={situacao === 'encerrando' ? 'orange' : 'gray'}>
                                    {getEncerramentoLabel(recorrencia)}
                                </span>
                            )}
                            {!!recorrencia.assinaturaId && (
                                <span className="sf-tag" data-tone="blue">
                                    Assinatura
                                </span>
                            )}
                        </span>
                    )}
                </span>

                <span className="rec-cell rec-cell-next">
                    <span className="rec-cell-label">Próximo</span>
                    {proximoStartAt ? (
                        <span className="rec-text">{formatProximo(proximoStartAt, recorrencia.timezone)}</span>
                    ) : (
                        <span className="rec-sub">Sem agendamentos futuros</span>
                    )}
                </span>

                <span className="rec-cell rec-cell-attendant">
                    <span className="rec-cell-label">Atendente</span>
                    <span className="rec-text">{recorrencia.attendantName || 'Não informado'}</span>
                </span>

                <span className="rec-cell rec-cell-progress" title={`${concluidos} de ${total} agendamentos concluídos`}>
                    <span className="rec-cell-label">Concluídos</span>
                    <span className="rec-text rec-numeric">
                        {concluidos} de {total}
                    </span>
                </span>

                <ChevronRight className="rec-chevron" aria-hidden />
            </Link>
        </li>
    );
};

export const RecorrenciasList = () => {
    const { recorrencias, loading, error, loadRecorrencias } = useRecorrencias();
    const { setParams } = useParamsContext();
    const [aba, setAba] = useState<Aba>('ativas');
    const [search, setSearch] = useState('');
    const [attendantId, setAttendantId] = useState('');
    const [visible, setVisible] = useState(PAGE_SIZE);

    const attendants = useMemo(() => {
        const map = new Map<string, { id: string; name: string; count: number }>();

        recorrencias.forEach((recorrencia) => {
            const current = map.get(recorrencia.attendantId);
            map.set(recorrencia.attendantId, {
                id: recorrencia.attendantId,
                name: recorrencia.attendantName || 'Não informado',
                count: (current?.count || 0) + 1,
            });
        });

        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [recorrencias]);

    // A busca vale para todas as abas: a contagem mostra onde esta o resultado.
    const filtradas = useMemo(() => {
        const term = normalizeSearch(search);

        return recorrencias.filter((recorrencia) => {
            if (attendantId && recorrencia.attendantId !== attendantId) return false;
            if (!term) return true;

            return normalizeSearch(
                [recorrencia.clientName, recorrencia.attendantName, ...getServiceNames(recorrencia)].join(' '),
            ).includes(term);
        });
    }, [recorrencias, search, attendantId]);

    const lista = useMemo(
        () => filtradas.filter((recorrencia) => pertenceAba(recorrencia, aba)).sort(sorters[aba]),
        [filtradas, aba],
    );

    useEffect(() => {
        setVisible(PAGE_SIZE);
    }, [aba, search, attendantId]);

    if (loading) {
        return (
            <div className="rec-page space-y-3" aria-busy="true">
                <div className="sf-skeleton h-10" />
                {[0, 1, 2, 3].map((item) => (
                    <div key={item} className="sf-skeleton h-20" />
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className="rec-page">
                <div className="sf-state">
                    <TriangleAlert aria-hidden />
                    <h2>Não foi possível carregar as recorrências</h2>
                    <button type="button" className="btn btn-primary btn-sm" onClick={loadRecorrencias}>
                        Tentar novamente
                    </button>
                </div>
            </div>
        );
    }

    if (!recorrencias.length) {
        return (
            <div className="rec-page">
                <div className="sf-state">
                    <RefreshCw aria-hidden />
                    <h2>Nenhuma recorrência criada</h2>
                    <p>Crie uma recorrência para agendar automaticamente os atendimentos de clientes fixos.</p>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setParams({ action: 'add' })}
                    >
                        Nova recorrência
                    </button>
                </div>
            </div>
        );
    }

    const hasFilters = !!search || !!attendantId;

    return (
        <div className="rec-page">
            <div className="rec-toolbar">
                <div className="sf-segmented rec-tabs" role="group" aria-label="Situação das recorrências">
                    {abas.map(({ key, label }) => (
                        <button key={key} type="button" aria-pressed={aba === key} onClick={() => setAba(key)}>
                            {label}
                            <span className="sf-segmented-count">
                                {filtradas.filter((recorrencia) => pertenceAba(recorrencia, key)).length}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="rec-filters">
                    <label className="sf-search">
                        <Search aria-hidden />
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar cliente ou serviço"
                            aria-label="Buscar cliente, serviço ou atendente"
                        />
                    </label>

                    {attendants.length > 1 && (
                        <select
                            className="sf-select-field"
                            value={attendantId}
                            onChange={(e) => setAttendantId(e.target.value)}
                            aria-label="Filtrar por atendente"
                        >
                            <option value="">Todos os atendentes</option>
                            {attendants.map(({ id, name, count }) => (
                                <option key={id} value={id}>
                                    {name} ({count})
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </div>

            {lista.length === 0 ? (
                <div className="sf-state">
                    {hasFilters ? <Search aria-hidden /> : <CircleCheck aria-hidden />}
                    <h2>{hasFilters ? 'Nenhum resultado nesta aba' : emptyMessages[aba]}</h2>
                    {hasFilters && (
                        <button
                            type="button"
                            className="sf-link"
                            onClick={() => {
                                setSearch('');
                                setAttendantId('');
                            }}
                        >
                            Limpar filtros
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="rec-head" aria-hidden>
                        <span>Cliente</span>
                        <span>Repetição</span>
                        <span>Próximo</span>
                        <span>Atendente</span>
                        <span>Concluídos</span>
                    </div>
                    <ul className="sf-group">
                        {lista.slice(0, visible).map((recorrencia) => (
                            <RecorrenciaRow key={recorrencia._id} recorrencia={recorrencia} />
                        ))}
                    </ul>
                    {lista.length > visible && (
                        <div className="mt-4 flex justify-center">
                            <button
                                type="button"
                                className="sf-link"
                                onClick={() => setVisible((current) => current + PAGE_SIZE)}
                            >
                                Mostrar mais ({lista.length - visible})
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
